/**
 * NeighborhoodAnalytics Component
 * 
 * Powerful analytics dashboard for neighboring properties.
 * Features:
 * - Cost per acre analysis by size bucket (small/medium/large farms)
 * - Owner concentration & family grouping with color coding
 * - Interactive SVG scatter plot with tooltips
 * - Market position insights for subject property
 * - Syncs with map for owner highlighting
 */

import React, { useState, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { View, Text, Pressable, ScrollView, Platform, useWindowDimensions } from 'react-native';
import { Ionicons } from '@expo/vector-icons';
import Svg, { Circle, Line, Text as SvgText, Rect, G, Path } from 'react-native-svg';
import { useStyles } from '../../hooks/useStyles';
import { useResponsive } from '../../shared/utils/responsive';
import { addColorOpacity } from '../../shared/utils/styles';
import { NeighborsByTier } from '../../shared/types/parcel';
import { GeoFeature } from '../../shared/types/parcel';
import { 
  clusterOwners, 
  calculateOwnershipStats, 
  OwnerCluster,
  ParcelOwnerData,
  OwnershipStats 
} from '../../shared/utils/ownerMatching';

// ============================================
// Types
// ============================================

interface NeighborhoodAnalyticsProps {
  neighborsByTier: NeighborsByTier[];
  primaryParcel?: GeoFeature | null;
  userAskPrice?: number | null;
  onOwnerSelect?: (ownerName: string, color: string) => void;
  selectedOwner?: string | null;
}

interface ParcelAnalysis {
  parcelId: string;
  ownerName: string;
  acres: number;
  landValue: number;
  buildingValue: number;
  totalValue: number;
  landPerAcre: number;
  totalPerAcre: number;
  tier: number;
  sizeBucket: 'small' | 'medium' | 'large' | 'xlarge';
  hasStructures: boolean;
}

type AnalyticsTab = 'overview' | 'bySize' | 'owners' | 'families' | 'chart';

// Size buckets for farms
const SIZE_BUCKETS = {
  small: { label: 'Small (<5 ac)', min: 0, max: 5, color: '#10B981' },
  medium: { label: 'Medium (5-25 ac)', min: 5, max: 25, color: '#3B82F6' },
  large: { label: 'Large (25-100 ac)', min: 25, max: 100, color: '#8B5CF6' },
  xlarge: { label: 'XLarge (100+ ac)', min: 100, max: Infinity, color: '#F59E0B' },
} as const;

function getSizeBucket(acres: number): keyof typeof SIZE_BUCKETS {
  if (acres < 5) return 'small';
  if (acres < 25) return 'medium';
  if (acres < 100) return 'large';
  return 'xlarge';
}

// ============================================
// Helper Functions
// ============================================

function formatCurrency(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return 'N/A';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    maximumFractionDigits: 0,
  }).format(value);
}

function formatPerAcre(value: number | null | undefined): string {
  if (value == null || isNaN(value)) return 'N/A';
  return `$${Math.round(value).toLocaleString()}/ac`;
}

function formatCompactNumber(value: number): string {
  if (value >= 1000000) return `$${(value / 1000000).toFixed(1)}M`;
  if (value >= 1000) return `$${(value / 1000).toFixed(0)}K`;
  return `$${value.toFixed(0)}`;
}

function median(arr: number[]): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 !== 0 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
}

function percentile(arr: number[], p: number): number {
  if (arr.length === 0) return 0;
  const sorted = [...arr].sort((a, b) => a - b);
  const idx = Math.ceil((p / 100) * sorted.length) - 1;
  return sorted[Math.max(0, idx)];
}

// ============================================
// Interactive Scatter Plot Component
// ============================================

interface ScatterPlotProps {
  data: ParcelAnalysis[];
  yKey: 'landPerAcre' | 'totalPerAcre';
  primaryValue?: { x: number; y: number } | null;
  askPricePerAcre?: number | null;
  ownerColors: Map<string, string>;
  selectedOwner?: string | null;
  onPointClick?: (parcel: ParcelAnalysis) => void;
  containerWidth: number;
}

function ScatterPlot({ 
  data, 
  yKey, 
  primaryValue, 
  askPricePerAcre, 
  ownerColors,
  selectedOwner,
  onPointClick,
  containerWidth 
}: ScatterPlotProps) {
  const styles = useStyles();
  const { isMobile } = useResponsive();
  const [hoveredPoint, setHoveredPoint] = useState<ParcelAnalysis | null>(null);
  
  // Responsive sizing - use actual container width
  const width = Math.max(300, Math.min(containerWidth - 32, 800));
  const height = isMobile ? 280 : 380;
  const padding = { top: 30, right: 30, bottom: 55, left: 70 };
  const plotWidth = width - padding.left - padding.right;
  const plotHeight = height - padding.top - padding.bottom;
  
  // Filter out invalid data and extreme outliers using IQR method
  const validData = useMemo(() => {
    const withValues = data.filter(d => {
      const y = d[yKey];
      return !isNaN(d.acres) && !isNaN(y) && d.acres > 0 && y > 0;
    });
    
    // Calculate IQR for outlier detection
    const yValues = withValues.map(d => d[yKey]);
    const q1 = percentile(yValues, 25);
    const q3 = percentile(yValues, 75);
    const iqr = q3 - q1;
    const upperBound = q3 + 3 * iqr; // 3x IQR for extreme outliers
    
    return withValues.filter(d => d[yKey] <= upperBound);
  }, [data, yKey]);
  
  // Calculate bounds
  const xValues = validData.map(d => d.acres);
  const yValues = validData.map(d => d[yKey]);
  
  if (primaryValue?.x) xValues.push(primaryValue.x);
  if (primaryValue?.y) yValues.push(primaryValue.y);
  if (askPricePerAcre) yValues.push(askPricePerAcre);
  
  const xMin = 0;
  const xMax = Math.max(...xValues) * 1.1 || 100;
  const yMin = 0;
  const yMax = Math.max(...yValues) * 1.15 || 50000;
  
  // Scale functions
  const scaleX = (val: number) => padding.left + ((val - xMin) / (xMax - xMin)) * plotWidth;
  const scaleY = (val: number) => padding.top + plotHeight - ((val - yMin) / (yMax - yMin)) * plotHeight;
  
  // Generate nice axis ticks
  const xTicks = generateNiceTicks(xMin, xMax, 6);
  const yTicks = generateNiceTicks(yMin, yMax, 5);
  
  return (
    <View>
      <Svg width={width} height={height}>
        {/* Background */}
        <Rect 
          x={padding.left} 
          y={padding.top} 
          width={plotWidth} 
          height={plotHeight} 
          fill={addColorOpacity(styles.tokens.color.background, 0.3)}
          stroke={styles.tokens.color.border}
          strokeWidth={1}
        />
        
        {/* Grid lines and Y-axis ticks */}
        {yTicks.map((tick, i) => (
          <G key={`y-${i}`}>
            <Line
              x1={padding.left}
              y1={scaleY(tick)}
              x2={padding.left + plotWidth}
              y2={scaleY(tick)}
              stroke={addColorOpacity(styles.tokens.color.border, 0.4)}
              strokeWidth={1}
              strokeDasharray="3,3"
            />
            <SvgText
              x={padding.left - 10}
              y={scaleY(tick) + 4}
              fontSize={11}
              fill={addColorOpacity(styles.tokens.color.onBackground, 0.7)}
              textAnchor="end"
            >
              {formatCompactNumber(tick)}
            </SvgText>
          </G>
        ))}
        
        {/* X-axis ticks */}
        {xTicks.map((tick, i) => (
          <G key={`x-${i}`}>
            <Line
              x1={scaleX(tick)}
              y1={padding.top + plotHeight}
              x2={scaleX(tick)}
              y2={padding.top + plotHeight + 6}
              stroke={styles.tokens.color.border}
              strokeWidth={1}
            />
            <SvgText
              x={scaleX(tick)}
              y={padding.top + plotHeight + 20}
              fontSize={11}
              fill={addColorOpacity(styles.tokens.color.onBackground, 0.7)}
              textAnchor="middle"
            >
              {tick >= 100 ? tick.toFixed(0) : tick.toFixed(1)}
            </SvgText>
          </G>
        ))}
        
        {/* Axis labels */}
        <SvgText
          x={padding.left + plotWidth / 2}
          y={height - 8}
          fontSize={12}
          fill={styles.tokens.color.onBackground}
          textAnchor="middle"
          fontWeight="600"
        >
          Parcel Size (acres)
        </SvgText>
        
        <SvgText
          x={16}
          y={padding.top + plotHeight / 2}
          fontSize={12}
          fill={styles.tokens.color.onBackground}
          textAnchor="middle"
          fontWeight="600"
          rotation={-90}
          originX={16}
          originY={padding.top + plotHeight / 2}
        >
          {yKey === 'landPerAcre' ? 'Land Value $/ac' : 'Total Value $/ac'}
        </SvgText>
        
        {/* Size bucket zones */}
        {Object.entries(SIZE_BUCKETS).map(([key, bucket]) => {
          const x1 = scaleX(Math.max(bucket.min, xMin));
          const x2 = scaleX(Math.min(bucket.max, xMax));
          if (x2 <= x1) return null;
          
          return (
            <Rect
              key={key}
              x={x1}
              y={padding.top}
              width={x2 - x1}
              height={plotHeight}
              fill={addColorOpacity(bucket.color, 0.05)}
            />
          );
        })}
        
        {/* Ask price reference line */}
        {askPricePerAcre && askPricePerAcre > 0 && askPricePerAcre <= yMax && (
          <G>
            <Line
              x1={padding.left}
              y1={scaleY(askPricePerAcre)}
              x2={padding.left + plotWidth}
              y2={scaleY(askPricePerAcre)}
              stroke="#F59E0B"
              strokeWidth={2}
              strokeDasharray="8,4"
            />
            <Rect
              x={padding.left + plotWidth - 65}
              y={scaleY(askPricePerAcre) - 12}
              width={60}
              height={18}
              fill="#F59E0B"
              rx={4}
            />
            <SvgText
              x={padding.left + plotWidth - 35}
              y={scaleY(askPricePerAcre) + 1}
              fontSize={10}
              fill="#FFF"
              textAnchor="middle"
              fontWeight="600"
            >
              Your Ask
            </SvgText>
          </G>
        )}
        
        {/* Data points - sorted so selected/hovered are on top */}
        {validData
          .sort((a, b) => {
            if (a.ownerName === selectedOwner) return 1;
            if (b.ownerName === selectedOwner) return -1;
            return 0;
          })
          .map((point, index) => {
            const x = scaleX(point.acres);
            const y = scaleY(point[yKey]);
            const color = ownerColors.get(point.ownerName) || SIZE_BUCKETS[point.sizeBucket].color;
            const isSelected = selectedOwner && point.ownerName === selectedOwner;
            const isHovered = hoveredPoint?.parcelId === point.parcelId;
            
            return (
              <Circle
                key={point.parcelId || index}
                cx={x}
                cy={y}
                r={isSelected ? 10 : isHovered ? 9 : 7}
                fill={color}
                stroke={isSelected || isHovered ? '#FFF' : addColorOpacity('#FFF', 0.8)}
                strokeWidth={isSelected ? 3 : isHovered ? 2.5 : 1.5}
                opacity={selectedOwner && !isSelected ? 0.4 : 0.9}
                onPress={() => onPointClick?.(point)}
              />
            );
          })}
        
        {/* Primary parcel marker */}
        {primaryValue && primaryValue.x > 0 && primaryValue.y > 0 && (
          <G>
            <Circle
              cx={scaleX(primaryValue.x)}
              cy={scaleY(primaryValue.y)}
              r={12}
              fill={styles.tokens.color.primary}
              stroke="#FFF"
              strokeWidth={3}
            />
            <SvgText
              x={scaleX(primaryValue.x)}
              y={scaleY(primaryValue.y) - 18}
              fontSize={11}
              fill={styles.tokens.color.primary}
              textAnchor="middle"
              fontWeight="700"
            >
              Subject
            </SvgText>
          </G>
        )}
      </Svg>
      
      {/* Size bucket legend */}
      <View style={{ 
        flexDirection: 'row', 
        justifyContent: 'center', 
        flexWrap: 'wrap',
        gap: 12,
        marginTop: 8,
        paddingHorizontal: 16,
      }}>
        {Object.entries(SIZE_BUCKETS).map(([key, bucket]) => (
          <View key={key} style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 10, height: 10, borderRadius: 5, backgroundColor: bucket.color }} />
            <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
              {bucket.label}
            </Text>
          </View>
        ))}
      </View>
    </View>
  );
}

function generateNiceTicks(min: number, max: number, targetCount: number): number[] {
  const range = max - min;
  const roughStep = range / (targetCount - 1);
  
  // Find a "nice" step size (1, 2, 5, 10, 20, 50, 100, etc.)
  const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep)));
  const residual = roughStep / magnitude;
  
  let niceStep: number;
  if (residual <= 1.5) niceStep = magnitude;
  else if (residual <= 3) niceStep = 2 * magnitude;
  else if (residual <= 7) niceStep = 5 * magnitude;
  else niceStep = 10 * magnitude;
  
  const niceMin = Math.floor(min / niceStep) * niceStep;
  const ticks: number[] = [];
  
  for (let tick = niceMin; tick <= max * 1.01; tick += niceStep) {
    if (tick >= min) ticks.push(tick);
    if (ticks.length >= targetCount + 2) break;
  }
  
  return ticks;
}

// ============================================
// Distribution Chart (Histogram)
// ============================================

interface DistributionChartProps {
  data: ParcelAnalysis[];
  valueKey: 'landPerAcre' | 'totalPerAcre';
  primaryValue: number | null;
  askPerAcre: number | null;
  containerWidth: number;
}

const PRICE_RANGES = [
  { min: 0, max: 2000, label: '<$2k' },
  { min: 2000, max: 5000, label: '$2-5k' },
  { min: 5000, max: 10000, label: '$5-10k' },
  { min: 10000, max: 15000, label: '$10-15k' },
  { min: 15000, max: 20000, label: '$15-20k' },
  { min: 20000, max: 30000, label: '$20-30k' },
  { min: 30000, max: Infinity, label: '>$30k' },
];

function DistributionChart({ data, valueKey, primaryValue, askPerAcre, containerWidth }: DistributionChartProps) {
  const styles = useStyles();
  const { isMobile } = useResponsive();
  
  const width = Math.min(containerWidth, 600);
  const height = isMobile ? 200 : 260;
  const padding = { top: 20, right: 16, bottom: 50, left: 40 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  // Count parcels in each range
  const histogram = useMemo(() => {
    return PRICE_RANGES.map(range => ({
      ...range,
      count: data.filter(d => {
        const val = d[valueKey];
        return val >= range.min && val < range.max;
      }).length,
    }));
  }, [data, valueKey]);
  
  const maxCount = Math.max(...histogram.map(h => h.count), 1);
  const barWidth = chartWidth / histogram.length - 8;
  
  // Find which bucket contains primary value
  const primaryBucketIndex = primaryValue 
    ? histogram.findIndex(h => primaryValue >= h.min && primaryValue < h.max)
    : -1;
    
  // Find which bucket contains ask price
  const askBucketIndex = askPerAcre
    ? histogram.findIndex(h => askPerAcre >= h.min && askPerAcre < h.max)
    : -1;
  
  return (
    <View style={{ alignItems: 'center' }}>
      <Svg width={width} height={height}>
        {/* Y axis */}
        <Line
          x1={padding.left}
          y1={padding.top}
          x2={padding.left}
          y2={padding.top + chartHeight}
          stroke={addColorOpacity(styles.tokens.color.onBackground, 0.2)}
          strokeWidth={1}
        />
        
        {/* Bars */}
        {histogram.map((bucket, index) => {
          const barHeight = (bucket.count / maxCount) * chartHeight;
          const x = padding.left + index * (chartWidth / histogram.length) + 4;
          const y = padding.top + chartHeight - barHeight;
          
          const isPrimary = index === primaryBucketIndex;
          const isAsk = index === askBucketIndex;
          
          return (
            <G key={bucket.label}>
              <Rect
                x={x}
                y={y}
                width={barWidth}
                height={barHeight}
                fill={
                  isPrimary ? styles.tokens.color.primary :
                  isAsk ? '#F59E0B' :
                  '#3B82F6'
                }
                rx={4}
                opacity={isPrimary || isAsk ? 1 : 0.7}
              />
              {/* Count label on top of bar */}
              {bucket.count > 0 && (
                <SvgText
                  x={x + barWidth / 2}
                  y={y - 5}
                  fontSize={11}
                  fill={styles.tokens.color.onBackground}
                  textAnchor="middle"
                  fontWeight="600"
                >
                  {bucket.count}
                </SvgText>
              )}
              {/* X axis label */}
              <SvgText
                x={x + barWidth / 2}
                y={padding.top + chartHeight + 16}
                fontSize={10}
                fill={addColorOpacity(styles.tokens.color.onBackground, 0.7)}
                textAnchor="middle"
              >
                {bucket.label}
              </SvgText>
            </G>
          );
        })}
        
        {/* X axis label */}
        <SvgText
          x={width / 2}
          y={height - 6}
          fontSize={12}
          fill={styles.tokens.color.onBackground}
          textAnchor="middle"
          fontWeight="600"
        >
          $/Acre Range
        </SvgText>
        
        {/* Y axis label */}
        <SvgText
          x={12}
          y={padding.top + chartHeight / 2}
          fontSize={12}
          fill={styles.tokens.color.onBackground}
          textAnchor="middle"
          fontWeight="600"
          rotation={-90}
          originX={12}
          originY={padding.top + chartHeight / 2}
        >
          # Parcels
        </SvgText>
      </Svg>
      
      {/* Legend */}
      <View style={{ flexDirection: 'row', gap: 16, marginTop: 8 }}>
        {primaryValue && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 12, height: 12, backgroundColor: styles.tokens.color.primary, borderRadius: 2 }} />
            <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
              Subject ({formatPerAcre(primaryValue)})
            </Text>
          </View>
        )}
        {askPerAcre && (
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 4 }}>
            <View style={{ width: 12, height: 12, backgroundColor: '#F59E0B', borderRadius: 2 }} />
            <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
              Ask ({formatPerAcre(askPerAcre)})
            </Text>
          </View>
        )}
      </View>
    </View>
  );
}

// ============================================
// Comparison Chart (Size Bucket Bars)
// ============================================

interface ComparisonChartProps {
  bucketAnalysis: Record<keyof typeof SIZE_BUCKETS, BucketAnalysis>;
  primaryBucket?: keyof typeof SIZE_BUCKETS;
  askPerAcre: number | null;
  containerWidth: number;
}

function ComparisonChart({ bucketAnalysis, primaryBucket, askPerAcre, containerWidth }: ComparisonChartProps) {
  const styles = useStyles();
  const { isMobile } = useResponsive();
  
  const width = Math.min(containerWidth, 600);
  const height = isMobile ? 220 : 280;
  const padding = { top: 30, right: 16, bottom: 50, left: 60 };
  const chartWidth = width - padding.left - padding.right;
  const chartHeight = height - padding.top - padding.bottom;
  
  const buckets = (Object.keys(SIZE_BUCKETS) as (keyof typeof SIZE_BUCKETS)[]);
  const maxValue = Math.max(...buckets.map(k => bucketAnalysis[k].medianPerAcre), askPerAcre || 0) * 1.1;
  
  const barHeight = chartHeight / buckets.length - 12;
  
  return (
    <View style={{ alignItems: 'center' }}>
      <Svg width={width} height={height}>
        {/* Grid lines */}
        {[0.25, 0.5, 0.75, 1].map((pct, i) => (
          <G key={i}>
            <Line
              x1={padding.left + chartWidth * pct}
              y1={padding.top}
              x2={padding.left + chartWidth * pct}
              y2={padding.top + chartHeight}
              stroke={addColorOpacity(styles.tokens.color.onBackground, 0.1)}
              strokeWidth={1}
              strokeDasharray="4,4"
            />
            <SvgText
              x={padding.left + chartWidth * pct}
              y={padding.top + chartHeight + 16}
              fontSize={10}
              fill={addColorOpacity(styles.tokens.color.onBackground, 0.6)}
              textAnchor="middle"
            >
              {formatCompactNumber(maxValue * pct)}
            </SvgText>
          </G>
        ))}
        
        {/* Bars */}
        {buckets.map((bucketKey, index) => {
          const bucket = SIZE_BUCKETS[bucketKey];
          const analysis = bucketAnalysis[bucketKey];
          const barWidth = (analysis.medianPerAcre / maxValue) * chartWidth;
          const y = padding.top + index * (chartHeight / buckets.length) + 6;
          const isPrimary = bucketKey === primaryBucket;
          
          return (
            <G key={bucketKey}>
              {/* Bar */}
              <Rect
                x={padding.left}
                y={y}
                width={Math.max(barWidth, 2)}
                height={barHeight}
                fill={bucket.color}
                rx={4}
                opacity={isPrimary ? 1 : 0.7}
                stroke={isPrimary ? '#FFF' : 'none'}
                strokeWidth={isPrimary ? 2 : 0}
              />
              
              {/* Value label on bar */}
              {analysis.medianPerAcre > 0 && (
                <SvgText
                  x={padding.left + Math.max(barWidth, 2) + 6}
                  y={y + barHeight / 2 + 4}
                  fontSize={11}
                  fill={styles.tokens.color.onBackground}
                  fontWeight="600"
                >
                  {formatPerAcre(analysis.medianPerAcre)} ({analysis.count})
                </SvgText>
              )}
              
              {/* Bucket label */}
              <SvgText
                x={padding.left - 6}
                y={y + barHeight / 2 + 4}
                fontSize={10}
                fill={addColorOpacity(styles.tokens.color.onBackground, 0.8)}
                textAnchor="end"
              >
                {bucket.label.replace(' ac)', ')').replace('XLarge', 'XL')}
              </SvgText>
            </G>
          );
        })}
        
        {/* Ask price reference line */}
        {askPerAcre && askPerAcre <= maxValue && (
          <G>
            <Line
              x1={padding.left + (askPerAcre / maxValue) * chartWidth}
              y1={padding.top - 10}
              x2={padding.left + (askPerAcre / maxValue) * chartWidth}
              y2={padding.top + chartHeight}
              stroke="#F59E0B"
              strokeWidth={2}
              strokeDasharray="6,3"
            />
            <Rect
              x={padding.left + (askPerAcre / maxValue) * chartWidth - 30}
              y={padding.top - 22}
              width={60}
              height={16}
              fill="#F59E0B"
              rx={3}
            />
            <SvgText
              x={padding.left + (askPerAcre / maxValue) * chartWidth}
              y={padding.top - 10}
              fontSize={9}
              fill="#FFF"
              textAnchor="middle"
              fontWeight="600"
            >
              Ask: {formatCompactNumber(askPerAcre)}
            </SvgText>
          </G>
        )}
        
        {/* X axis label */}
        <SvgText
          x={width / 2}
          y={height - 6}
          fontSize={12}
          fill={styles.tokens.color.onBackground}
          textAnchor="middle"
          fontWeight="600"
        >
          Median $/Acre
        </SvgText>
      </Svg>
      
      {/* Info text */}
      {primaryBucket && (
        <View style={{
          backgroundColor: addColorOpacity(SIZE_BUCKETS[primaryBucket].color, 0.1),
          padding: styles.utils.spacing.sm,
          borderRadius: styles.utils.radius.sm,
          marginTop: styles.utils.spacing.sm,
          borderLeftWidth: 3,
          borderLeftColor: SIZE_BUCKETS[primaryBucket].color,
        }}>
          <Text style={[styles.text('sm'), { color: styles.tokens.color.onBackground }]}>
            Your subject property is in the <Text style={{ fontWeight: '700' }}>{SIZE_BUCKETS[primaryBucket].label}</Text> bucket
            with a median of <Text style={{ fontWeight: '700' }}>{formatPerAcre(bucketAnalysis[primaryBucket].medianPerAcre)}</Text>
          </Text>
        </View>
      )}
    </View>
  );
}

// ============================================
// Size Bucket Analysis Card
// ============================================

interface BucketAnalysis {
  bucket: keyof typeof SIZE_BUCKETS;
  count: number;
  totalAcres: number;
  avgLandPerAcre: number;
  avgTotalPerAcre: number;
  medianPerAcre: number;
  minPerAcre: number;
  maxPerAcre: number;
}

function SizeBucketCard({ 
  analysis, 
  isSelected,
  onSelect 
}: { 
  analysis: BucketAnalysis;
  isSelected?: boolean;
  onSelect?: () => void;
}) {
  const styles = useStyles();
  const bucket = SIZE_BUCKETS[analysis.bucket];
  
  if (analysis.count === 0) return null;
  
  return (
    <Pressable
      onPress={onSelect}
      style={({ pressed }) => ({
        flex: 1,
        minWidth: 140,
        padding: styles.utils.spacing.md,
        backgroundColor: isSelected 
          ? addColorOpacity(bucket.color, 0.2)
          : pressed 
            ? addColorOpacity(bucket.color, 0.1)
            : styles.tokens.color.surface,
        borderRadius: styles.utils.radius.md,
        borderWidth: 2,
        borderColor: isSelected ? bucket.color : 'transparent',
      })}
    >
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginBottom: 8 }}>
        <View style={{ 
          width: 12, 
          height: 12, 
          borderRadius: 6, 
          backgroundColor: bucket.color 
        }} />
        <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.semibold }]}>
          {bucket.label}
        </Text>
      </View>
      
      <Text style={[styles.heading('lg'), { color: bucket.color }]}>
        {formatPerAcre(analysis.medianPerAcre)}
      </Text>
      <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
        median $/acre
      </Text>
      
      <View style={{ marginTop: 8, gap: 2 }}>
        <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
          Range: {formatPerAcre(analysis.minPerAcre)} - {formatPerAcre(analysis.maxPerAcre)}
        </Text>
        <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
          {analysis.count} parcels • {analysis.totalAcres.toFixed(0)} ac
        </Text>
      </View>
    </Pressable>
  );
}

// ============================================
// Owner Row Component
// ============================================

interface OwnerRowProps {
  owner: {
    name: string;
    clusterType: OwnerCluster['clusterType'];
    parcels: number;
    acres: number;
    value: number;
    percentOfArea: number;
    color: string;
    avgPerAcre?: number;
  };
  index: number;
  isSelected?: boolean;
  onSelect?: () => void;
}

function OwnerRow({ owner, index, isSelected, onSelect }: OwnerRowProps) {
  const styles = useStyles();
  
  return (
    <Pressable
      onPress={onSelect}
      style={({ pressed }) => ({
        flexDirection: 'row',
        alignItems: 'center',
        paddingVertical: styles.utils.spacing.sm,
        paddingHorizontal: styles.utils.spacing.sm,
        marginHorizontal: -styles.utils.spacing.sm,
        backgroundColor: isSelected 
          ? addColorOpacity(owner.color, 0.2)
          : pressed 
            ? addColorOpacity(owner.color, 0.1)
            : 'transparent',
        borderRadius: styles.utils.radius.sm,
        borderBottomWidth: 1,
        borderBottomColor: styles.tokens.color.border,
      })}
    >
      {/* Color indicator & rank */}
      <View style={{
        width: 28,
        height: 28,
        borderRadius: 14,
        backgroundColor: owner.color,
        alignItems: 'center',
        justifyContent: 'center',
        marginRight: styles.utils.spacing.sm,
      }}>
        <Text style={[styles.text('xs'), { fontWeight: styles.utils.typography.weights.bold, color: '#FFF' }]}>
          {index + 1}
        </Text>
      </View>
      
      <View style={{ flex: 1 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
          <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.medium }]} numberOfLines={1}>
            {owner.name}
          </Text>
          {owner.clusterType === 'family' && (
            <View style={{ backgroundColor: addColorOpacity('#F59E0B', 0.2), paddingHorizontal: 5, paddingVertical: 1, borderRadius: 3 }}>
              <Text style={{ fontSize: 9, color: '#B45309', fontWeight: '600' }}>FAMILY</Text>
            </View>
          )}
          {owner.clusterType === 'entity' && (
            <View style={{ backgroundColor: addColorOpacity('#6366F1', 0.2), paddingHorizontal: 5, paddingVertical: 1, borderRadius: 3 }}>
              <Text style={{ fontSize: 9, color: '#4338CA', fontWeight: '600' }}>ENTITY</Text>
            </View>
          )}
        </View>
        <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
          {owner.parcels} parcel{owner.parcels > 1 ? 's' : ''} • {owner.acres.toFixed(1)} ac • {owner.percentOfArea.toFixed(0)}%
        </Text>
      </View>
      
      <View style={{ alignItems: 'flex-end' }}>
        <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.semibold }]}>
          {formatCurrency(owner.value)}
        </Text>
        {owner.avgPerAcre && (
          <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
            {formatPerAcre(owner.avgPerAcre)}
          </Text>
        )}
      </View>
    </Pressable>
  );
}

// ============================================
// Main Component
// ============================================

export function NeighborhoodAnalytics({ 
  neighborsByTier, 
  primaryParcel, 
  userAskPrice,
  onOwnerSelect,
  selectedOwner,
}: NeighborhoodAnalyticsProps) {
  const styles = useStyles();
  const { isMobile } = useResponsive();
  const { width: windowWidth } = useWindowDimensions();
  const [activeTab, setActiveTab] = useState<AnalyticsTab>('overview');
  const [showLandOnly, setShowLandOnly] = useState(false);
  const [containerWidth, setContainerWidth] = useState(windowWidth - 64);
  const [chartType, setChartType] = useState<'scatter' | 'distribution' | 'comparison'>('scatter');
  
  // Measure container width
  const handleLayout = useCallback((e: any) => {
    const width = e.nativeEvent?.layout?.width;
    if (width && width > 0) {
      setContainerWidth(width);
    }
  }, []);
  
  // Process all neighbors into analysis data
  const analysisData = useMemo<ParcelAnalysis[]>(() => {
    const data: ParcelAnalysis[] = [];
    
    for (const tierData of neighborsByTier) {
      for (const neighborEntry of tierData.neighbors) {
        const n = (neighborEntry as any).neighbor || neighborEntry;
        
        const acres = Number(n.area_acres) || 0;
        const landValue = Number(n.land_value) || 0;
        const totalValue = Number(n.total_value) || 0;
        // Compute building value if not provided
        const buildingValue = Number(n.building_value) || Math.max(0, totalValue - landValue);
        
        if (acres > 0 && totalValue > 0) {
          // Better structure detection:
          // - Building value > $10k OR
          // - Building value > 15% of total value (for expensive land areas)
          const buildingRatio = totalValue > 0 ? buildingValue / totalValue : 0;
          const hasStructures = buildingValue > 10000 || buildingRatio > 0.15;
          
          data.push({
            parcelId: n.parcel_id || `${n.id}`,
            ownerName: n.owner_name || 'Unknown',
            acres,
            landValue,
            buildingValue,
            totalValue,
            landPerAcre: landValue > 0 ? landValue / acres : totalValue / acres,
            totalPerAcre: totalValue / acres,
            tier: tierData.tier,
            sizeBucket: getSizeBucket(acres),
            hasStructures,
          });
        }
      }
    }
    
    return data;
  }, [neighborsByTier]);
  
  // Analyze by size bucket
  const bucketAnalysis = useMemo<Record<keyof typeof SIZE_BUCKETS, BucketAnalysis>>(() => {
    const result: Record<string, BucketAnalysis> = {};
    
    for (const key of Object.keys(SIZE_BUCKETS) as (keyof typeof SIZE_BUCKETS)[]) {
      const parcels = analysisData.filter(d => d.sizeBucket === key);
      const values = parcels.map(d => showLandOnly ? d.landPerAcre : d.totalPerAcre);
      
      result[key] = {
        bucket: key,
        count: parcels.length,
        totalAcres: parcels.reduce((sum, d) => sum + d.acres, 0),
        avgLandPerAcre: parcels.length > 0 
          ? parcels.reduce((sum, d) => sum + d.landPerAcre, 0) / parcels.length 
          : 0,
        avgTotalPerAcre: parcels.length > 0 
          ? parcels.reduce((sum, d) => sum + d.totalPerAcre, 0) / parcels.length 
          : 0,
        medianPerAcre: median(values),
        minPerAcre: values.length > 0 ? Math.min(...values) : 0,
        maxPerAcre: values.length > 0 ? Math.max(...values) : 0,
      };
    }
    
    return result as Record<keyof typeof SIZE_BUCKETS, BucketAnalysis>;
  }, [analysisData, showLandOnly]);
  
  // Owner clustering with enhanced data
  const { clusters, ownershipStats, ownerColors, topOwnersEnhanced } = useMemo(() => {
    const parcelData: ParcelOwnerData[] = analysisData.map(d => ({
      parcelId: d.parcelId,
      ownerName: d.ownerName,
      acres: d.acres,
      value: d.totalValue,
      tier: d.tier,
    }));
    
    const clusters = clusterOwners(parcelData, 0.85);
    const totalAcres = analysisData.reduce((sum, d) => sum + d.acres, 0);
    const stats = calculateOwnershipStats(clusters, totalAcres);
    
    // Build color map
    const colorMap = new Map<string, string>();
    for (const cluster of clusters) {
      for (const member of cluster.members) {
        colorMap.set(member.rawName, cluster.color);
        colorMap.set(member.normalizedName, cluster.color);
      }
    }
    
    // Enhance top owners with $/acre
    const enhanced = stats.topOwners.map(owner => {
      const ownerParcels = analysisData.filter(d => d.ownerName === owner.name);
      const avgPerAcre = ownerParcels.length > 0
        ? ownerParcels.reduce((sum, d) => sum + d.totalPerAcre, 0) / ownerParcels.length
        : 0;
      return { ...owner, avgPerAcre };
    });
    
    return { clusters, ownershipStats: stats, ownerColors: colorMap, topOwnersEnhanced: enhanced };
  }, [analysisData]);
  
  // Calculate aggregate metrics
  const metrics = useMemo(() => {
    if (analysisData.length === 0) return null;
    
    const withStructures = analysisData.filter(d => d.hasStructures);
    const landOnly = analysisData.filter(d => !d.hasStructures);
    
    const allPerAcre = analysisData.map(d => showLandOnly ? d.landPerAcre : d.totalPerAcre);
    
    return {
      totalParcels: analysisData.length,
      totalAcres: analysisData.reduce((sum, d) => sum + d.acres, 0),
      totalValue: analysisData.reduce((sum, d) => sum + d.totalValue, 0),
      uniqueOwners: ownershipStats.totalClusters,
      
      medianPerAcre: median(allPerAcre),
      avgPerAcre: allPerAcre.reduce((a, b) => a + b, 0) / allPerAcre.length,
      minPerAcre: Math.min(...allPerAcre),
      maxPerAcre: Math.max(...allPerAcre),
      
      withStructuresCount: withStructures.length,
      landOnlyCount: landOnly.length,
      
      withStructuresMedian: median(withStructures.map(d => d.totalPerAcre)),
      landOnlyMedian: median(landOnly.map(d => d.totalPerAcre)),
    };
  }, [analysisData, ownershipStats, showLandOnly]);
  
  // Primary parcel analysis
  const primaryAnalysis = useMemo(() => {
    if (!primaryParcel) return null;
    
    const acres = Number(primaryParcel.area_acres) || 0;
    const landValue = Number(primaryParcel.land_value) || 0;
    const totalValue = Number(primaryParcel.total_value) || 0;
    
    if (acres === 0) return null;
    
    return {
      acres,
      sizeBucket: getSizeBucket(acres),
      landPerAcre: landValue > 0 ? landValue / acres : totalValue / acres,
      totalPerAcre: totalValue / acres,
    };
  }, [primaryParcel]);
  
  // Ask price per acre
  const askPerAcre = useMemo(() => {
    if (!userAskPrice || !primaryAnalysis) return null;
    return userAskPrice / primaryAnalysis.acres;
  }, [userAskPrice, primaryAnalysis]);
  
  // Handle owner selection
  const handleOwnerClick = useCallback((ownerName: string) => {
    const color = ownerColors.get(ownerName) || '#6B7280';
    onOwnerSelect?.(ownerName, color);
  }, [ownerColors, onOwnerSelect]);
  
  if (!metrics || analysisData.length === 0) {
    return null;
  }
  
  const tabs: { key: AnalyticsTab; label: string; icon: string }[] = [
    { key: 'overview', label: 'Overview', icon: 'stats-chart-outline' },
    { key: 'bySize', label: 'By Size', icon: 'resize-outline' },
    { key: 'owners', label: 'Owners', icon: 'people-outline' },
    { key: 'families', label: 'Families', icon: 'git-network-outline' },
    { key: 'chart', label: 'Chart', icon: 'scatter-chart' },
  ];
  
  return (
    <View onLayout={handleLayout} style={{ marginTop: styles.utils.spacing.sm }}>
      {/* Tab Bar */}
      <ScrollView 
        horizontal 
        showsHorizontalScrollIndicator={false}
        style={{ marginBottom: styles.utils.spacing.md }}
      >
        <View style={{ flexDirection: 'row', gap: styles.utils.spacing.xs }}>
          {tabs.map(tab => (
            <Pressable
              key={tab.key}
              onPress={() => setActiveTab(tab.key)}
              style={({ pressed }) => ({
                flexDirection: 'row',
                alignItems: 'center',
                gap: 6,
                paddingHorizontal: styles.utils.spacing.md,
                paddingVertical: styles.utils.spacing.sm,
                borderRadius: styles.utils.radius.md,
                backgroundColor: activeTab === tab.key 
                  ? styles.tokens.color.primary 
                  : pressed
                    ? addColorOpacity(styles.tokens.color.primary, 0.1)
                    : addColorOpacity(styles.tokens.color.surface, 0.8),
              })}
            >
              <Ionicons 
                name={tab.icon as any} 
                size={16} 
                color={activeTab === tab.key ? styles.tokens.color.onPrimary : styles.tokens.color.onSurface} 
              />
              <Text style={[styles.text('sm'), { 
                color: activeTab === tab.key ? styles.tokens.color.onPrimary : styles.tokens.color.onSurface,
                fontWeight: activeTab === tab.key ? styles.utils.typography.weights.semibold : styles.utils.typography.weights.regular,
              }]}>
                {tab.label}
              </Text>
            </Pressable>
          ))}
        </View>
      </ScrollView>
      
      {/* Toggle for land vs total */}
      <View style={{ 
        flexDirection: 'row', 
        alignItems: 'center', 
        marginBottom: styles.utils.spacing.md,
        gap: styles.utils.spacing.sm,
      }}>
        <Text style={[styles.text('sm'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.7) }]}>
          Values:
        </Text>
        <Pressable
          onPress={() => setShowLandOnly(false)}
          style={{
            paddingHorizontal: 12,
            paddingVertical: 4,
            borderRadius: styles.utils.radius.sm,
            backgroundColor: !showLandOnly ? styles.tokens.color.primary : styles.tokens.color.surface,
          }}
        >
          <Text style={[styles.text('xs'), { 
            color: !showLandOnly ? styles.tokens.color.onPrimary : styles.tokens.color.onSurface,
            fontWeight: !showLandOnly ? styles.utils.typography.weights.semibold : styles.utils.typography.weights.regular,
          }]}>
            Total
          </Text>
        </Pressable>
        <Pressable
          onPress={() => setShowLandOnly(true)}
          style={{
            paddingHorizontal: 12,
            paddingVertical: 4,
            borderRadius: styles.utils.radius.sm,
            backgroundColor: showLandOnly ? '#10B981' : styles.tokens.color.surface,
          }}
        >
          <Text style={[styles.text('xs'), { 
            color: showLandOnly ? '#FFF' : styles.tokens.color.onSurface,
            fontWeight: showLandOnly ? styles.utils.typography.weights.semibold : styles.utils.typography.weights.regular,
          }]}>
            Land Only
          </Text>
        </Pressable>
      </View>
      
      {/* Tab Content */}
      {activeTab === 'overview' && (
        <View>
          {/* Key Metrics */}
          <View style={{ 
            flexDirection: 'row', 
            flexWrap: 'wrap', 
            gap: styles.utils.spacing.sm,
            marginBottom: styles.utils.spacing.lg,
          }}>
            <View style={{
              flex: 1,
              minWidth: 120,
              padding: styles.utils.spacing.md,
              backgroundColor: addColorOpacity('#3B82F6', 0.1),
              borderRadius: styles.utils.radius.md,
              borderLeftWidth: 4,
              borderLeftColor: '#3B82F6',
            }}>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                Median $/Acre
              </Text>
              <Text style={[styles.heading('lg'), { color: '#3B82F6' }]}>
                {formatPerAcre(metrics.medianPerAcre)}
              </Text>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
                Range: {formatPerAcre(metrics.minPerAcre)} - {formatPerAcre(metrics.maxPerAcre)}
              </Text>
            </View>
            
            <View style={{
              flex: 1,
              minWidth: 120,
              padding: styles.utils.spacing.md,
              backgroundColor: addColorOpacity('#10B981', 0.1),
              borderRadius: styles.utils.radius.md,
              borderLeftWidth: 4,
              borderLeftColor: '#10B981',
            }}>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                Land Only ({metrics.landOnlyCount})
              </Text>
              <Text style={[styles.heading('lg'), { color: '#10B981' }]}>
                {formatPerAcre(metrics.landOnlyMedian)}
              </Text>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
                median for unimproved
              </Text>
            </View>
            
            <View style={{
              flex: 1,
              minWidth: 120,
              padding: styles.utils.spacing.md,
              backgroundColor: addColorOpacity('#8B5CF6', 0.1),
              borderRadius: styles.utils.radius.md,
              borderLeftWidth: 4,
              borderLeftColor: '#8B5CF6',
            }}>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                With Structures ({metrics.withStructuresCount})
              </Text>
              <Text style={[styles.heading('lg'), { color: '#8B5CF6' }]}>
                {metrics.withStructuresCount > 0 ? formatPerAcre(metrics.withStructuresMedian) : 'N/A'}
              </Text>
              <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
                median for improved
              </Text>
            </View>
          </View>
          
          {/* Subject Property Position */}
          {primaryAnalysis && (
            <View style={{
              padding: styles.utils.spacing.md,
              backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.1),
              borderRadius: styles.utils.radius.md,
              borderLeftWidth: 4,
              borderLeftColor: styles.tokens.color.primary,
            }}>
              <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.semibold, marginBottom: styles.utils.spacing.sm }]}>
                📍 Subject Property Position
              </Text>
              
              <View style={{ flexDirection: 'row', flexWrap: 'wrap', gap: styles.utils.spacing.lg }}>
                <View>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                    Size Bucket
                  </Text>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <View style={{ 
                      width: 10, 
                      height: 10, 
                      borderRadius: 5, 
                      backgroundColor: SIZE_BUCKETS[primaryAnalysis.sizeBucket].color 
                    }} />
                    <Text style={[styles.text('base'), { fontWeight: styles.utils.typography.weights.semibold }]}>
                      {SIZE_BUCKETS[primaryAnalysis.sizeBucket].label}
                    </Text>
                  </View>
                </View>
                
                <View>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                    Tax Value $/Acre
                  </Text>
                  <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.bold }]}>
                    {formatPerAcre(showLandOnly ? primaryAnalysis.landPerAcre : primaryAnalysis.totalPerAcre)}
                  </Text>
                  {bucketAnalysis[primaryAnalysis.sizeBucket].count > 0 && (
                    <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
                      Bucket median: {formatPerAcre(bucketAnalysis[primaryAnalysis.sizeBucket].medianPerAcre)}
                    </Text>
                  )}
                </View>
                
                {askPerAcre && (
                  <View>
                    <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6) }]}>
                      Your Ask $/Acre
                    </Text>
                    <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.bold, color: '#F59E0B' }]}>
                      {formatPerAcre(askPerAcre)}
                    </Text>
                    <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
                      {askPerAcre > metrics.medianPerAcre
                        ? `${((askPerAcre / metrics.medianPerAcre - 1) * 100).toFixed(0)}% above market`
                        : `${((1 - askPerAcre / metrics.medianPerAcre) * 100).toFixed(0)}% below market`
                      }
                    </Text>
                  </View>
                )}
              </View>
            </View>
          )}
        </View>
      )}
      
      {activeTab === 'bySize' && (
        <View>
          <Text style={[styles.text('sm'), { 
            color: addColorOpacity(styles.tokens.color.onBackground, 0.7), 
            marginBottom: styles.utils.spacing.md 
          }]}>
            Compare similar-sized parcels for accurate valuation
          </Text>
          
          <View style={{ 
            flexDirection: 'row', 
            flexWrap: 'wrap', 
            gap: styles.utils.spacing.sm,
          }}>
            {(Object.keys(SIZE_BUCKETS) as (keyof typeof SIZE_BUCKETS)[]).map(key => (
              <SizeBucketCard 
                key={key}
                analysis={bucketAnalysis[key]}
                isSelected={primaryAnalysis?.sizeBucket === key}
              />
            ))}
          </View>
          
          {primaryAnalysis && (
            <View style={{
              marginTop: styles.utils.spacing.lg,
              padding: styles.utils.spacing.md,
              backgroundColor: addColorOpacity(styles.tokens.color.primary, 0.08),
              borderRadius: styles.utils.radius.md,
            }}>
              <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.semibold }]}>
                💡 For your {primaryAnalysis.acres.toFixed(1)} acre property:
              </Text>
              <Text style={[styles.text('sm'), { 
                color: addColorOpacity(styles.tokens.color.onBackground, 0.8),
                marginTop: 4,
              }]}>
                Compare against the "{SIZE_BUCKETS[primaryAnalysis.sizeBucket].label}" bucket where 
                the median is {formatPerAcre(bucketAnalysis[primaryAnalysis.sizeBucket].medianPerAcre)}.
                {askPerAcre && bucketAnalysis[primaryAnalysis.sizeBucket].medianPerAcre > 0 && (
                  askPerAcre > bucketAnalysis[primaryAnalysis.sizeBucket].medianPerAcre
                    ? ` Your ask is ${((askPerAcre / bucketAnalysis[primaryAnalysis.sizeBucket].medianPerAcre - 1) * 100).toFixed(0)}% above similar parcels.`
                    : ` Your ask is ${((1 - askPerAcre / bucketAnalysis[primaryAnalysis.sizeBucket].medianPerAcre) * 100).toFixed(0)}% below similar parcels.`
                )}
              </Text>
            </View>
          )}
        </View>
      )}
      
      {activeTab === 'owners' && (
        <View>
          <Text style={[styles.text('xs'), { 
            color: addColorOpacity(styles.tokens.color.onBackground, 0.6), 
            marginBottom: styles.utils.spacing.md 
          }]}>
            Tap an owner to highlight on map • {ownershipStats.totalClusters} unique owners/entities
          </Text>
          
          <ScrollView style={{ maxHeight: isMobile ? 400 : 500 }}>
            {topOwnersEnhanced.map((owner, index) => (
              <OwnerRow
                key={`${owner.name}-${index}`}
                owner={owner}
                index={index}
                isSelected={selectedOwner === owner.name}
                onSelect={() => handleOwnerClick(owner.name)}
              />
            ))}
          </ScrollView>
        </View>
      )}
      
      {activeTab === 'families' && (
        <View>
          <Text style={[styles.text('xs'), { 
            color: addColorOpacity(styles.tokens.color.onBackground, 0.6), 
            marginBottom: styles.utils.spacing.md 
          }]}>
            Owners grouped by likely family relationships • Same color = same family/entity
          </Text>
          
          {/* Family Stats Summary */}
          <View style={{ 
            flexDirection: 'row', 
            flexWrap: 'wrap', 
            gap: styles.utils.spacing.sm, 
            marginBottom: styles.utils.spacing.lg 
          }}>
            {(() => {
              const familyClusters = clusters.filter(c => c.clusterType === 'family');
              const entityClusters = clusters.filter(c => c.clusterType === 'entity');
              const individualClusters = clusters.filter(c => c.clusterType === 'same_person');
              return (
                <>
                  <View style={{ 
                    padding: styles.utils.spacing.sm, 
                    backgroundColor: addColorOpacity('#F59E0B', 0.15), 
                    borderRadius: styles.utils.radius.sm,
                    borderLeftWidth: 3,
                    borderLeftColor: '#F59E0B',
                  }}>
                    <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.bold, color: '#B45309' }]}>
                      {familyClusters.length}
                    </Text>
                    <Text style={[styles.text('xs'), { color: '#92400E' }]}>Family Groups</Text>
                  </View>
                  <View style={{ 
                    padding: styles.utils.spacing.sm, 
                    backgroundColor: addColorOpacity('#6366F1', 0.15), 
                    borderRadius: styles.utils.radius.sm,
                    borderLeftWidth: 3,
                    borderLeftColor: '#6366F1',
                  }}>
                    <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.bold, color: '#4338CA' }]}>
                      {entityClusters.length}
                    </Text>
                    <Text style={[styles.text('xs'), { color: '#3730A3' }]}>Entities/Trusts</Text>
                  </View>
                  <View style={{ 
                    padding: styles.utils.spacing.sm, 
                    backgroundColor: addColorOpacity('#10B981', 0.15), 
                    borderRadius: styles.utils.radius.sm,
                    borderLeftWidth: 3,
                    borderLeftColor: '#10B981',
                  }}>
                    <Text style={[styles.text('lg'), { fontWeight: styles.utils.typography.weights.bold, color: '#047857' }]}>
                      {individualClusters.length}
                    </Text>
                    <Text style={[styles.text('xs'), { color: '#065F46' }]}>Individuals</Text>
                  </View>
                </>
              );
            })()}
          </View>
          
          {/* Family Groups List */}
          <ScrollView style={{ maxHeight: isMobile ? 400 : 500 }}>
            {clusters
              .sort((a, b) => b.totalAcres - a.totalAcres)
              .map((cluster, index) => (
              <Pressable
                key={cluster.id}
                onPress={() => handleOwnerClick(cluster.representativeName)}
                style={({ pressed }) => ({
                  flexDirection: 'row',
                  paddingVertical: styles.utils.spacing.sm,
                  paddingHorizontal: styles.utils.spacing.sm,
                  marginHorizontal: -styles.utils.spacing.sm,
                  backgroundColor: selectedOwner === cluster.representativeName 
                    ? addColorOpacity(cluster.color, 0.2)
                    : pressed 
                      ? addColorOpacity(cluster.color, 0.1)
                      : 'transparent',
                  borderRadius: styles.utils.radius.sm,
                  borderBottomWidth: 1,
                  borderBottomColor: styles.tokens.color.border,
                })}
              >
                {/* Color dot */}
                <View style={{
                  width: 28,
                  height: 28,
                  borderRadius: 14,
                  backgroundColor: cluster.color,
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginRight: styles.utils.spacing.sm,
                }}>
                  <Ionicons 
                    name={
                      cluster.clusterType === 'family' ? 'people' :
                      cluster.clusterType === 'entity' ? 'business' : 'person'
                    } 
                    size={14} 
                    color="#FFF" 
                  />
                </View>
                
                <View style={{ flex: 1 }}>
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                    <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.medium }]} numberOfLines={1}>
                      {cluster.representativeName}
                    </Text>
                    <View style={{ 
                      backgroundColor: 
                        cluster.clusterType === 'family' ? addColorOpacity('#F59E0B', 0.2) :
                        cluster.clusterType === 'entity' ? addColorOpacity('#6366F1', 0.2) :
                        addColorOpacity('#10B981', 0.2),
                      paddingHorizontal: 5, 
                      paddingVertical: 1, 
                      borderRadius: 3 
                    }}>
                      <Text style={{ 
                        fontSize: 9, 
                        color: 
                          cluster.clusterType === 'family' ? '#B45309' :
                          cluster.clusterType === 'entity' ? '#4338CA' : '#047857',
                        fontWeight: '600' 
                      }}>
                        {cluster.clusterType === 'family' ? 'FAMILY' : 
                         cluster.clusterType === 'entity' ? 'ENTITY' : 'INDIVIDUAL'}
                      </Text>
                    </View>
                  </View>
                  
                  {/* Members list for families/entities with multiple members */}
                  {cluster.members.length > 1 && (
                    <Text style={[styles.text('xs'), { 
                      color: addColorOpacity(styles.tokens.color.onBackground, 0.5),
                      marginTop: 2,
                    }]} numberOfLines={2}>
                      {cluster.members.slice(0, 3).map(m => m.rawName).join(' • ')}
                      {cluster.members.length > 3 && ` +${cluster.members.length - 3} more`}
                    </Text>
                  )}
                  
                  <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.6), marginTop: 2 }]}>
                    {cluster.totalParcels} parcel{cluster.totalParcels > 1 ? 's' : ''} • {cluster.totalAcres.toFixed(1)} ac • {((cluster.totalAcres / metrics!.totalAcres) * 100).toFixed(0)}% of area
                  </Text>
                </View>
                
                <View style={{ alignItems: 'flex-end' }}>
                  <Text style={[styles.text('sm'), { fontWeight: styles.utils.typography.weights.semibold }]}>
                    {formatCurrency(cluster.totalValue)}
                  </Text>
                  <Text style={[styles.text('xs'), { color: addColorOpacity(styles.tokens.color.onBackground, 0.5) }]}>
                    {formatPerAcre(cluster.totalAcres > 0 ? cluster.totalValue / cluster.totalAcres : 0)}
                  </Text>
                </View>
              </Pressable>
            ))}
          </ScrollView>
        </View>
      )}
      
      {activeTab === 'chart' && (
        <View>
          {/* Chart Type Toggle */}
          <View style={{ 
            flexDirection: 'row', 
            gap: styles.utils.spacing.xs, 
            marginBottom: styles.utils.spacing.md 
          }}>
            {[
              { key: 'scatter', label: 'Size vs Price', icon: 'scatter-chart' },
              { key: 'distribution', label: 'Distribution', icon: 'bar-chart-outline' },
              { key: 'comparison', label: 'Comparison', icon: 'stats-chart-outline' },
            ].map(ct => (
              <Pressable
                key={ct.key}
                onPress={() => setChartType(ct.key as typeof chartType)}
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 4,
                  paddingHorizontal: styles.utils.spacing.sm,
                  paddingVertical: 4,
                  borderRadius: styles.utils.radius.sm,
                  backgroundColor: chartType === ct.key ? styles.tokens.color.primary : styles.tokens.color.surface,
                  borderWidth: 1,
                  borderColor: chartType === ct.key ? styles.tokens.color.primary : styles.tokens.color.border,
                }}
              >
                <Ionicons 
                  name={ct.icon as any} 
                  size={14} 
                  color={chartType === ct.key ? styles.tokens.color.onPrimary : styles.tokens.color.onSurface} 
                />
                <Text style={[styles.text('xs'), { 
                  color: chartType === ct.key ? styles.tokens.color.onPrimary : styles.tokens.color.onSurface,
                  fontWeight: chartType === ct.key ? styles.utils.typography.weights.semibold : styles.utils.typography.weights.regular,
                }]}>
                  {ct.label}
                </Text>
              </Pressable>
            ))}
          </View>
          
          {chartType === 'scatter' && (
            <View>
              <Text style={[styles.text('xs'), { 
                color: addColorOpacity(styles.tokens.color.onBackground, 0.6), 
                marginBottom: styles.utils.spacing.sm 
              }]}>
                Larger parcels typically have lower $/acre • Colors = size buckets • Click to select
              </Text>
              
              <ScatterPlot
                data={analysisData}
                yKey={showLandOnly ? 'landPerAcre' : 'totalPerAcre'}
                primaryValue={primaryAnalysis ? {
                  x: primaryAnalysis.acres,
                  y: showLandOnly ? primaryAnalysis.landPerAcre : primaryAnalysis.totalPerAcre,
                } : null}
                askPricePerAcre={showLandOnly ? undefined : askPerAcre}
                ownerColors={ownerColors}
                selectedOwner={selectedOwner}
                onPointClick={(parcel) => handleOwnerClick(parcel.ownerName)}
                containerWidth={containerWidth}
              />
            </View>
          )}
          
          {chartType === 'distribution' && (
            <View>
              <Text style={[styles.text('xs'), { 
                color: addColorOpacity(styles.tokens.color.onBackground, 0.6), 
                marginBottom: styles.utils.spacing.sm 
              }]}>
                How many parcels fall into each $/acre range
              </Text>
              
              <DistributionChart
                data={analysisData}
                valueKey={showLandOnly ? 'landPerAcre' : 'totalPerAcre'}
                primaryValue={primaryAnalysis ? (showLandOnly ? primaryAnalysis.landPerAcre : primaryAnalysis.totalPerAcre) : null}
                askPerAcre={askPerAcre}
                containerWidth={containerWidth}
              />
            </View>
          )}
          
          {chartType === 'comparison' && (
            <View>
              <Text style={[styles.text('xs'), { 
                color: addColorOpacity(styles.tokens.color.onBackground, 0.6), 
                marginBottom: styles.utils.spacing.sm 
              }]}>
                Compare median $/acre across different property sizes
              </Text>
              
              <ComparisonChart
                bucketAnalysis={bucketAnalysis}
                primaryBucket={primaryAnalysis?.sizeBucket}
                askPerAcre={askPerAcre}
                containerWidth={containerWidth}
              />
            </View>
          )}
        </View>
      )}
    </View>
  );
}

export default NeighborhoodAnalytics;
